import axios from 'axios';
import { axiosInstance } from '../clients/axiosClient';
import { PHARMA_API_PREFIX, API_V1_PREFIX } from '../utils/constants';
import { GetTuliBookingInfoResponse } from '../types/apiGeneratedTypes';

const getTokenFromQuery = () => {
  // First try to get token from URL parameters
  const urlParams = new URLSearchParams(window.location.search);
  const token = urlParams.get('token');

  // If token exists in URL, return it
  if (token && token.length > 0) {
    return token;
  }

  // If no token in URL, try to get from sessionStorage
  const storedToken = sessionStorage.getItem('temp_token');
  if (storedToken) {
    // Update URL with the stored token
    const newUrl = `${window.location.pathname}?token=${storedToken}`;
    window.history.replaceState({}, '', newUrl);
    return storedToken;
  }

  return null;
};

// Create a new axios instance specifically for pharmacy calls
const pharmacyAxiosInstance = axios.create({
  baseURL: axiosInstance.defaults.baseURL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add request interceptor to handle token from URL
pharmacyAxiosInstance.interceptors.request.use((config) => {
  const token = getTokenFromQuery();
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const bookPharmacySlot = async (newPharmacyBookingRequestObject: {
  member_id?: string;
  pharmacy_id: string;
  test_date: string;
}): Promise<any> => {
  try {
    const { data } = await pharmacyAxiosInstance.post(
      `${API_V1_PREFIX}${PHARMA_API_PREFIX}`,
      {
        member_id: newPharmacyBookingRequestObject.member_id,
        pharmacy_id: newPharmacyBookingRequestObject.pharmacy_id,
        test_date: newPharmacyBookingRequestObject.test_date
      }
    );

    return data;
  } catch (error: any) {
    if (error.response?.data?.detail) {
      throw error.response.data;
    }
    throw error;
  }
};

export const searchPharmacies = async (postcode: string): Promise<any> => {
  try {
    const { data } = await pharmacyAxiosInstance.get(
      `${API_V1_PREFIX}${PHARMA_API_PREFIX}search/`,
      {
        params: { postcode }
      }
    );

    return data;
  } catch (error: any) {
    if (error.response?.data?.detail) {
      throw error.response.data;
    }
    throw error;
  }
};

export const getAvailability = async (pharmaId: string): Promise<any> => {
  try {
    const { data } = await pharmacyAxiosInstance.get(
      `${API_V1_PREFIX}${PHARMA_API_PREFIX}slots/`,
      {
        params: { pharmacy_id: pharmaId }
      }
    );

    return data;
  } catch (error: any) {
    if (error.response?.data?.detail) {
      throw error.response.data;
    }
    throw error;
  }
};

export const resentTuliBooking = async (memberId: string): Promise<any> => {
  try {
    const { data } = await axiosInstance.post(
      `${API_V1_PREFIX}members/resend-tuli-booking`,
      { member_id: memberId }
    );

    return data;
  } catch (error: any) {
    if (error.response && error.response.data && error.response.data.detail) {
      throw new Error(error.response.data.detail);
    }
    throw new Error(error);
  }
};

export const getTuliBookingInfo = async (
  bookingId: number
): Promise<GetTuliBookingInfoResponse> => {
  try {
    const { data } = await axiosInstance.get(
      `${API_V1_PREFIX}${PHARMA_API_PREFIX}booking/${bookingId}`
    );
    return data;
  } catch (error: any) {
    if (error.response && error.response.data && error.response.data.detail) {
      throw new Error(error.response.data.detail);
    }
    throw new Error(error);
  }
};

export const getInuviBookingInfo = async (
  appointmentId: number
): Promise<any> => {
  try {
    const { data } = await axiosInstance.get(
      `${API_V1_PREFIX}appointment/inuvi-booking`,
      {
        params: { appointment_id: appointmentId }
      }
    );
    
    // Return null if no data is present
    if (!data) {
      return null;
    }
    
    return data;
  } catch (error: any) {
    if (error.response && error.response.data && error.response.data.detail) {
      throw new Error(error.response.data.detail);
    }
    throw new Error(error);
  }
};

export const validateToken = async (token: string): Promise<any> => {
  try {
    const { data } = await pharmacyAxiosInstance.post(
      `${API_V1_PREFIX}members/validate-token`,
      { token }
    );

    return data;
  } catch (error: any) {
    if (error.response?.data?.detail) {
      throw error.response.data;
    }
    throw error;
  }
};

export const bookHomeBloodAppointment = async (params: {
  firstDateTime: string;
  secondDateTime: string;
  thirdDateTime: string;
}): Promise<any> => {
  try {
    const { data } = await pharmacyAxiosInstance.post(
      `${API_V1_PREFIX}appointment/home-blood`,
      {
        firstDateTime: params.firstDateTime,
        secondDateTime: params.secondDateTime,
        thirdDateTime: params.thirdDateTime,
      }
    );

    return data;
  } catch (error: any) {
    if (error.response?.data?.detail) {
      throw error.response.data;
    }
    throw error;
  }
};
